// Out-of-hearts funnel: purchase prompt · Wait · Watch ad · End run.

import React, { useEffect, useMemo, useState } from "react";
import {
    HEART_REFILL_MS,
    MAX_HEARTS,
    formatHeartRefillCountdown,
    formatHeartsMeter,
    getHeartRefillMsRemaining,
} from "../utils/heartEconomy";
import {
    getHeartRefillProductId,
    getHeartSingleProductId,
    getHeartTenProductId,
    getIapProductById,
} from "../utils/iapCatalog";
import { getIapStoreStatus } from "../utils/iapStore";

const HeartEmptyOverlay = ({
    hearts = 0,
    heartLastRefillAt = Date.now(),
    maxHearts = MAX_HEARTS,
    refillMs = HEART_REFILL_MS,
    busyAction = "",
    error = "",
    adStatus = null,
    adsRemoved = false,
    onWatchAd,
    onBuyProduct,
    onEndRun,
}) => {
    const [now, setNow] = useState(Date.now());
    const storeStatus = getIapStoreStatus();

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const msRemaining = getHeartRefillMsRemaining(
        hearts,
        heartLastRefillAt,
        now,
        maxHearts,
        refillMs
    );
    const countdown = formatHeartRefillCountdown(msRemaining);
    const adAvailable = Boolean(adStatus?.available);
    const adRemaining =
        adStatus && Number.isFinite(Number(adStatus.remainingToday))
            ? Number(adStatus.remainingToday)
            : null;
    const isBusy = Boolean(busyAction);
    const safeHearts = Math.max(0, Math.floor(Number(hearts) || 0));

    const packs = useMemo(() => {
        const singleId = getHeartSingleProductId();
        const tripleId = getHeartRefillProductId();
        const tenId = getHeartTenProductId();
        const single = getIapProductById(singleId);
        const triple = getIapProductById(tripleId);
        const ten = getIapProductById(tenId);
        return [
            {
                id: singleId,
                title: single?.title || "+1 Heart",
                price: single?.previewPriceLabel || "$0.99",
                busyKey: "iap1",
            },
            {
                id: tripleId,
                title: triple?.title || "+3 Hearts",
                price: triple?.previewPriceLabel || "$1.99",
                busyKey: "iap3",
            },
            {
                id: tenId,
                title: ten?.title || "+10 Hearts",
                price: ten?.previewPriceLabel || "$7.99",
                busyKey: "iap10",
            },
        ];
    }, []);

    return (
        <div
            className="heartEmptyOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="heartEmptyTitle"
        >
            <div className="heartEmptyOverlay__panel">
                <div id="heartEmptyTitle" className="heartEmptyOverlay__title">
                    Out of hearts
                </div>

                <p className="heartEmptyOverlay__message">
                    You need a heart to keep this run going. Would you like to
                    purchase hearts?
                </p>

                {storeStatus?.isPreview !== false ? (
                    <div className="heartEmptyOverlay__previewBanner" role="status">
                        Open Beta preview — buys and ads are free test grants (no
                        real charges).
                    </div>
                ) : null}

                <div
                    className="heartEmptyOverlay__hearts"
                    aria-label={`${safeHearts} hearts`}
                >
                    {formatHeartsMeter(safeHearts, maxHearts)}
                </div>

                <div className="heartEmptyOverlay__purchase" aria-label="Purchase hearts">
                    {packs.map((pack) => (
                        <button
                            key={pack.id}
                            type="button"
                            className="heartEmptyOverlay__button heartEmptyOverlay__button--iap"
                            onClick={() => onBuyProduct?.(pack.id)}
                            disabled={isBusy || typeof onBuyProduct !== "function"}
                        >
                            {busyAction === pack.busyKey
                                ? "Buying…"
                                : `${pack.title} · ${pack.price}`}
                        </button>
                    ))}
                    <p className="heartEmptyOverlay__hint">
                        Adds to your stash (can go above {maxHearts})
                        {storeStatus?.isPreview !== false
                            ? " · preview (no charge)"
                            : ""}
                    </p>
                </div>

                <div className="heartEmptyOverlay__wait" role="status">
                    <span className="heartEmptyOverlay__waitLabel">
                        Or wait — next heart in
                    </span>
                    <span className="heartEmptyOverlay__waitValue">
                        {countdown}
                    </span>
                </div>

                {error ? (
                    <p className="heartEmptyOverlay__error" role="alert">
                        {error}
                    </p>
                ) : null}

                <div className="heartEmptyOverlay__actions">
                    {adsRemoved ? null : (
                        <>
                            <button
                                type="button"
                                className="heartEmptyOverlay__button heartEmptyOverlay__button--primary"
                                onClick={onWatchAd}
                                disabled={isBusy || !adAvailable}
                            >
                                {busyAction === "ad"
                                    ? "Watching…"
                                    : adAvailable
                                      ? "Watch Ad · +1 Heart"
                                      : "Ad limit reached"}
                            </button>
                            {adRemaining != null && adAvailable ? (
                                <p className="heartEmptyOverlay__hint">
                                    {adRemaining} ad
                                    {adRemaining === 1 ? "" : "s"} left today
                                    {adStatus?.isPreview ? " · preview" : ""}
                                </p>
                            ) : null}
                        </>
                    )}

                    <button
                        type="button"
                        className="heartEmptyOverlay__button heartEmptyOverlay__button--secondary"
                        onClick={onEndRun}
                        disabled={isBusy}
                    >
                        End Run
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HeartEmptyOverlay;
